// Pure derivation of "next steps" checklists for teacher and student journeys.
// Platform-agnostic: routes are passed in by the caller.
// Mirrored in bmc-marketplace-mobile/bmc-shared/src/lib/nextSteps.ts — keep in sync.

export type StepStatus = 'todo' | 'pending' | 'done';

export interface NextStep {
  id: string;
  label: string;
  /** Shown for the first incomplete step (CTA hint / pending explanation). */
  hint?: string;
  status: StepStatus;
  /** Deep link for the step's CTA. */
  route: string;
}

export interface TeacherStepSignals {
  /** hasDraft || hasActiveVersion */
  hasProfile: boolean;
  /** hasActiveVersion */
  profileApproved: boolean;
  /** draftStatus === 'PENDING_REVIEW' (no active version yet) */
  profilePendingReview: boolean;
  /** draftStatus === 'REJECTED' */
  profileRejected: boolean;
  offeringCount: number;
  hasPublishedOffering: boolean;
  /** teacher has at least one inquiry thread */
  hasInquiries: boolean;
  /** teacher has replied on any thread */
  hasRepliedToInquiry: boolean;
}

export interface TeacherStepRoutes {
  profileSetup: string;
  createOffering: string;
  offerings: string;
  inquiries: string;
}

export function deriveTeacherSteps(s: TeacherStepSignals, r: TeacherStepRoutes): NextStep[] {
  const profileStatus: StepStatus = s.hasProfile ? 'done' : 'todo';

  let approvedStatus: StepStatus = 'todo';
  let approvedHint: string | undefined;
  if (s.profileApproved) {
    approvedStatus = 'done';
  } else if (s.profilePendingReview) {
    approvedStatus = 'pending';
    approvedHint = 'Your profile is under review. We\u2019ll notify you once it\u2019s approved.';
  } else if (s.profileRejected) {
    approvedHint = 'Your profile needs revision. Fix the flagged sections and resubmit.';
  } else if (s.hasProfile) {
    approvedHint = 'Complete all sections and submit your profile for review.';
  }

  const offeringStatus: StepStatus = s.offeringCount > 0 ? 'done' : 'todo';
  const publishedStatus: StepStatus = s.hasPublishedOffering ? 'done' : 'todo';
  const replyStatus: StepStatus = s.hasRepliedToInquiry
    ? 'done'
    : s.hasInquiries
      ? 'todo'
      : 'pending';

  return [
    {
      id: 'create-profile',
      label: 'Create your profile',
      status: profileStatus,
      route: r.profileSetup,
    },
    {
      id: 'profile-approved',
      label: 'Get your profile approved',
      hint: approvedHint,
      status: approvedStatus,
      route: r.profileSetup,
    },
    {
      id: 'first-offering',
      label: 'Create your first offering',
      hint: 'Offerings tell students what you teach, how, and at what price.',
      status: offeringStatus,
      route: r.createOffering,
    },
    {
      id: 'offering-published',
      label: 'Publish an offering',
      hint: 'Submit your offering for review. Once approved and published, students can find you.',
      status: publishedStatus,
      route: r.offerings,
    },
    {
      id: 'reply-inquiries',
      label: 'Respond to student inquiries',
      hint: s.hasInquiries
        ? 'You have inquiries waiting \u2014 quick replies improve your conversion.'
        : 'Once students discover you, their inquiries will appear here.',
      status: replyStatus,
      route: r.inquiries,
    },
  ];
}

export interface StudentStepSignals {
  goalCompleted: boolean;
  /** local flag set when the search page is visited */
  hasSearched: boolean;
  /** student has at least one inquiry thread */
  hasInquiries: boolean;
  /** any thread with a teacher reply / active conversation / unread messages */
  hasTeacherReply: boolean;
}

export interface StudentStepRoutes {
  goalOnboarding: string;
  search: string;
  inquiries: string;
}

export function deriveStudentSteps(s: StudentStepSignals, r: StudentStepRoutes): NextStep[] {
  // Having sent an inquiry implies the student has searched.
  const searched = s.hasSearched || s.hasInquiries;

  return [
    {
      id: 'create-goal',
      label: 'Set your learning goal',
      status: s.goalCompleted ? 'done' : 'todo',
      route: r.goalOnboarding,
    },
    {
      id: 'search-teachers',
      label: 'Browse teachers for your goal',
      hint: 'Search results are pre-filtered to match your subject and grade.',
      status: searched ? 'done' : 'todo',
      route: r.search,
    },
    {
      id: 'first-inquiry',
      label: 'Send your first inquiry',
      hint: 'Found someone promising? Send an inquiry to start the conversation.',
      status: s.hasInquiries ? 'done' : 'todo',
      route: r.search,
    },
    {
      id: 'track-replies',
      label: 'Hear back from a teacher',
      hint: 'Teachers usually reply within a day \u2014 check your inquiries for updates.',
      status: s.hasTeacherReply ? 'done' : s.hasInquiries ? 'pending' : 'todo',
      route: r.inquiries,
    },
  ];
}

/** True when every step is done — used to allow dismissing the checklist. */
export function allStepsDone(steps: NextStep[]): boolean {
  return steps.every((s) => s.status === 'done');
}

/** The first step the user should act on (first non-done). */
export function currentStep(steps: NextStep[]): NextStep | undefined {
  return steps.find((s) => s.status !== 'done');
}
